import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { Button, Form, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TeamHeader } from '../header/TeamHeader';
import { Loading } from '../../../ui/loading/Loading';
import FilterComponent from '../../../ui/FilterComponent';
import { getOrganizationTeam } from '../../../../store/user_organizations/teams/team';
import { zynity_api } from '../../../../config/external-routes';

type DocumentLibraryType = 'link' | 'external_doc' | 'internal_doc';

type DocumentLibraryItem = {
    id: number;
    type: DocumentLibraryType;
    source_provider: string;
    title: string;
    description: string | null;
    display_name: string | null;
    external_url: string | null;
    storage_path: string | null;
    mime_type: string | null;
    file_size_bytes: number | null;
    author: {
        id: number;
        name: string;
    } | null;
    date_added: string | null;
    date_revised: string | null;
    download_url: string | null;
    where_used: Array<{
        usage_id: number;
        context_type: string;
        context_id: number | null;
        context_parent_id: number | null;
        context_route: string | null;
        context_label: string | null;
    }>;
};

type DocumentLibraryPagination = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
};

const browserPreviewableExtensions = new Set([
    'pdf',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'bmp',
    'svg',
    'tif',
    'tiff',
    'avif',
    'ico',
]);

const typeLabels: Record<DocumentLibraryType, string> = {
    link: 'Link',
    external_doc: 'External Doc',
    internal_doc: 'Internal Doc',
};

const CreateDocumentModal = ({
    show,
    onHide,
    onCreated,
    organizationId,
    teamId,
    item,
}: {
    show: boolean;
    onHide: () => void;
    onCreated: (mode: 'created' | 'updated') => void;
    organizationId: string | undefined;
    teamId: string | undefined;
    item: DocumentLibraryItem | null;
}) => {
    const authenticatedUser = useSelector((state: any) => state.auth);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [type, setType] = useState<DocumentLibraryType>('internal_doc');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [externalUrl, setExternalUrl] = useState('');
    const [uploadedTempFile, setUploadedTempFile] = useState<{ file_name: string; display_name: string } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const isEditing = item !== null;

    useEffect(() => {
        if (!show) {
            setType('internal_doc');
            setTitle('');
            setDescription('');
            setExternalUrl('');
            setUploadedTempFile(null);
            setUploading(false);
            setSaving(false);
            return;
        }

        setType(item?.type ?? 'internal_doc');
        setTitle(item?.title ?? '');
        setDescription(item?.description ?? '');
        setExternalUrl(item?.external_url ?? '');
        setUploadedTempFile(null);
        setUploading(false);
        setSaving(false);
    }, [show, item]);

    const uploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const formData = new FormData();
        formData.append('file', file, file.name);
        setUploading(true);

        try {
            const response = await axios.post(zynity_api.global_upload_temp_files, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${authenticatedUser.access_token}`,
                },
            });

            setUploadedTempFile(response.data);
            if (title.trim() === '') {
                setTitle(response.data.display_name);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Upload failed',
                text: 'The file could not be uploaded to the temporary storage area.',
            });
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        if (!organizationId || !teamId) {
            return;
        }

        if (type === 'internal_doc' && !uploadedTempFile && (!isEditing || item?.type !== 'internal_doc')) {
            Swal.fire({
                icon: 'warning',
                title: 'File required',
                text: 'Select a file before saving this internal document.',
            });
            return;
        }

        if (type !== 'internal_doc' && externalUrl.trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'URL required',
                text: 'Enter a URL before creating a link or external document.',
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                type,
                title: title.trim() === '' ? null : title.trim(),
                description: description.trim() === '' ? null : description.trim(),
                external_url: type === 'internal_doc' ? null : externalUrl.trim(),
                file_name: uploadedTempFile?.file_name ?? null,
                display_name: type === 'internal_doc'
                    ? (uploadedTempFile?.display_name ?? item?.display_name ?? null)
                    : null,
            };

            if (isEditing && item) {
                await axios.put(
                    zynity_api.organization_team_document_library_item
                        .replace('{organization_id}', organizationId)
                        .replace('{team_id}', teamId)
                        .replace('{item_id}', String(item.id)),
                    payload
                );
                onCreated('updated');
            } else {
                await axios.post(
                    zynity_api.organization_team_document_library
                        .replace('{organization_id}', organizationId)
                        .replace('{team_id}', teamId),
                    payload
                );
                onCreated('created');
            }
            onHide();
        } catch (error: any) {
            const message = error?.response?.data?.error ?? 'The document could not be saved.';
            Swal.fire({
                icon: 'error',
                title: isEditing ? 'Update failed' : 'Creation failed',
                text: typeof message === 'string' ? message : 'The document could not be saved.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{isEditing ? 'Edit Document' : 'Add Document'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Type</Form.Label>
                    <Form.Select value={type} onChange={(e) => setType(e.target.value as DocumentLibraryType)}>
                        <option value="internal_doc">Internal Document</option>
                        <option value="link">Link</option>
                        <option value="external_doc">External Document</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Document title"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional description"
                    />
                </Form.Group>

                {type === 'internal_doc' ? (
                    <Form.Group className="mb-3">
                        <Form.Label>File</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                className="btn-teal"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                            >
                                {uploadedTempFile ? 'Replace File' : 'Choose File'}
                            </Button>
                            <span className="text-muted small">
                                {uploading
                                    ? 'Uploading...'
                                    : uploadedTempFile?.display_name ?? item?.display_name ?? 'No file selected'}
                            </span>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={uploadFile}
                        />
                    </Form.Group>
                ) : (
                    <Form.Group className="mb-3">
                        <Form.Label>URL</Form.Label>
                        <Form.Control
                            type="url"
                            value={externalUrl}
                            onChange={(e) => setExternalUrl(e.target.value)}
                            placeholder="https://..."
                        />
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button className="btn-teal" onClick={handleSubmit} disabled={saving || uploading}>
                    {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const UsageListModal = ({
    show,
    onHide,
    item,
    onDetachUsage,
    detachingUsageId,
}: {
    show: boolean;
    onHide: () => void;
    item: DocumentLibraryItem | null;
    onDetachUsage: (item: DocumentLibraryItem, usageId: number, usageLabel: string) => void;
    detachingUsageId: number | null;
}) => {
    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Where Used</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="fw-bold mb-3">{item?.title ?? 'Document'}</div>
                {!item || item.where_used.length === 0 ? (
                    <div className="text-muted">This item is not attached anywhere yet.</div>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {item.where_used.map((usage) => (
                            <div key={usage.usage_id} className="border rounded p-3 d-flex justify-content-between align-items-start gap-3">
                                <div>
                                    <div className="fw-bold">{usage.context_label || usage.context_type}</div>
                                    <div className="small text-muted mb-1">{usage.context_type}</div>
                                    {usage.context_route && (
                                        <a href={usage.context_route} target="_blank" rel="noreferrer">
                                            Open location
                                        </a>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={detachingUsageId === usage.usage_id}
                                    onClick={() => onDetachUsage(item, usage.usage_id, usage.context_label || usage.context_type)}
                                >
                                    {detachingUsageId === usage.usage_id ? 'Detaching...' : 'Detach'}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export const DocumentLibraryPage = () => {
    const { organization_id, team_id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const organizationTeam = useSelector((state: any) => state.organization_team);

    const [items, setItems] = useState<DocumentLibraryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingItem, setEditingItem] = useState<DocumentLibraryItem | null>(null);
    const [usageItem, setUsageItem] = useState<DocumentLibraryItem | null>(null);
    const [detachingUsageId, setDetachingUsageId] = useState<number | null>(null);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);
    const [pagination, setPagination] = useState<DocumentLibraryPagination>({
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: null,
        to: null,
    });

    useEffect(() => {
        dispatch(getOrganizationTeam({ organization_id, team_id }) as any);
    }, [dispatch, organization_id, team_id]);

    const fetchItems = async (page = pagination.current_page, perPage = pagination.per_page) => {
        if (!organization_id || !team_id) {
            return;
        }

        setLoading(true);
        try {
            let url = zynity_api.organization_team_document_library
                .replace('{organization_id}', organization_id)
                .replace('{team_id}', team_id);

            const params: Record<string, string | number> = {
                limit: perPage,
                page,
            };
            if (filterText && filterText.trim() !== '') {
                params.q = filterText.trim();
            }
            if (typeFilter !== 'all') {
                params.type = typeFilter;
            }

            const response = await axios.get(url, { params });
            setItems(response.data.data ?? []);
            setPagination({
                current_page: response.data.current_page ?? page,
                last_page: response.data.last_page ?? 1,
                per_page: response.data.per_page ?? perPage,
                total: response.data.total ?? 0,
                from: response.data.from ?? null,
                to: response.data.to ?? null,
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Load failed',
                text: 'The library could not be loaded for this team.',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!organization_id || !team_id) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setPaginationResetToggle((current) => !current);
            fetchItems(1, pagination.per_page);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [organization_id, team_id, filterText, typeFilter]);

    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => setFilterText('');
        const handleFilter = (e: { target: { value: string } }) => setFilterText(e.target.value);

        return (
            <div className="d-flex flex-wrap align-items-center gap-2">
                <FilterComponent onFilter={handleFilter} onClear={handleClear} filterText={filterText} />
                <Form.Select
                    style={{ width: '180px', height: '34px' }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    <option value="all">All types</option>
                    <option value="internal_doc">Internal documents</option>
                    <option value="external_doc">External documents</option>
                    <option value="link">Links</option>
                </Form.Select>
            </div>
        );
    }, [filterText, typeFilter]);

    const openExternal = (item: DocumentLibraryItem) => {
        if (!item.external_url) {
            return;
        }

        window.open(item.external_url, '_blank', 'noopener,noreferrer');
    };

    const canOpenInternalInBrowser = (item: DocumentLibraryItem) => {
        if (item.type !== 'internal_doc') {
            return false;
        }

        const hasDownloadUrl = typeof item.download_url === 'string' && item.download_url.trim() !== '';
        const hasStoragePath = typeof item.storage_path === 'string' && item.storage_path.trim() !== '';
        if (!hasDownloadUrl || !hasStoragePath) {
            return false;
        }

        const normalizedMimeType = (item.mime_type || '')
            .toLowerCase()
            .split(';')[0]
            .trim();
        if (normalizedMimeType === 'application/pdf' || normalizedMimeType.startsWith('image/')) {
            return true;
        }

        const fileNameCandidate = item.display_name || item.storage_path || '';
        const normalizedFileName = fileNameCandidate.split('?')[0].split('#')[0];
        const extension = normalizedFileName.includes('.')
            ? normalizedFileName.split('.').pop()?.toLowerCase() || ''
            : '';

        return browserPreviewableExtensions.has(extension);
    };

    const shouldShowOpenButton = (item: DocumentLibraryItem) => {
        if (item.type === 'internal_doc') {
            return canOpenInternalInBrowser(item);
        }

        return typeof item.external_url === 'string' && item.external_url.trim() !== '';
    };

    const openInternal = async (item: DocumentLibraryItem, forceDownload = false) => {
        if (!organization_id || !team_id || !item.download_url) {
            return;
        }

        try {
            const response = await axios.get(
                zynity_api.organization_team_document_library_download
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
                    .replace('{item_id}', String(item.id)),
                {
                    params: forceDownload ? {} : { disposition: 'inline' },
                    responseType: 'blob',
                }
            );

            const blob = new Blob([response.data], { type: response.headers['content-type'] || item.mime_type || 'application/octet-stream' });
            const objectUrl = window.URL.createObjectURL(blob);

            if (forceDownload) {
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = item.display_name || item.title || `document-${item.id}`;
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                window.open(objectUrl, '_blank', 'noopener,noreferrer');
            }

            window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: forceDownload ? 'Download failed' : 'Open failed',
                text: 'The selected document could not be retrieved.',
            });
        }
    };

    const handleDeleteItem = async (item: DocumentLibraryItem) => {
        if (!organization_id || !team_id) {
            return;
        }

        if (item.where_used.length > 0) {
            Swal.fire({
                icon: 'info',
                title: 'Document in use',
                text: 'Detach this item from all current locations before deleting it.',
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Delete document?',
            text: 'This will remove the item from the library.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await axios.delete(
                zynity_api.organization_team_document_library_item
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
                    .replace('{item_id}', String(item.id))
            );

            const nextPage = items.length === 1 && pagination.current_page > 1
                ? pagination.current_page - 1
                : pagination.current_page;
            if (nextPage !== pagination.current_page) {
                setPaginationResetToggle((current) => !current);
            }
            fetchItems(nextPage, pagination.per_page);
        } catch (error: any) {
            const message = error?.response?.data?.error ?? 'The document could not be deleted.';
            Swal.fire({
                icon: 'error',
                title: 'Delete failed',
                text: typeof message === 'string' ? message : 'The document could not be deleted.',
            });
        }
    };

    const handleDetachUsage = async (item: DocumentLibraryItem, usageId: number, usageLabel: string) => {
        if (!organization_id || !team_id) {
            return;
        }

        const result = await Swal.fire({
            title: 'Detach document?',
            text: `This will remove the document from "${usageLabel}".`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Detach',
        });

        if (!result.isConfirmed) {
            return;
        }

        setDetachingUsageId(usageId);
        try {
            await axios.delete(
                zynity_api.organization_team_document_library_item
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
                    .replace('{item_id}', String(item.id)) + `/attachments/${usageId}`
            );

            setItems((currentItems) => currentItems.map((currentItem) => {
                if (currentItem.id !== item.id) {
                    return currentItem;
                }

                return {
                    ...currentItem,
                    where_used: currentItem.where_used.filter((usage) => usage.usage_id !== usageId),
                };
            }));

            setUsageItem((currentItem) => {
                if (!currentItem || currentItem.id !== item.id) {
                    return currentItem;
                }

                return {
                    ...currentItem,
                    where_used: currentItem.where_used.filter((usage) => usage.usage_id !== usageId),
                };
            });
        } catch (error: any) {
            const message = error?.response?.data?.error ?? 'The document usage could not be detached.';
            Swal.fire({
                icon: 'error',
                title: 'Detach failed',
                text: typeof message === 'string' ? message : 'The document usage could not be detached.',
            });
        } finally {
            setDetachingUsageId(null);
        }
    };

    const columns = [
        {
            name: 'Title',
            selector: (row: DocumentLibraryItem) => row.title,
            sortable: true,
            wrap: true,
            grow: 1.5,
        },
        {
            name: 'Description',
            selector: (row: DocumentLibraryItem) => row.description || '',
            wrap: true,
            grow: 2,
        },
        {
            name: 'Type',
            selector: (row: DocumentLibraryItem) => typeLabels[row.type],
            maxWidth: '140px',
        },
        {
            name: 'Author',
            selector: (row: DocumentLibraryItem) => row.author?.name || 'Unknown',
            maxWidth: '160px',
            wrap: true,
        },
        {
            name: 'Where Used',
            cell: (row: DocumentLibraryItem) => (
                <div className="py-2">
                    {row.where_used.length === 0 ? (
                        <span className="text-muted">Not attached yet</span>
                    ) : (
                        <>
                            {row.where_used.slice(0, 2).map((usage) => (
                                <div key={usage.usage_id} className="small">
                                    {usage.context_route ? (
                                        <a href={usage.context_route} target="_blank" rel="noreferrer">
                                            {usage.context_label || usage.context_type}
                                        </a>
                                    ) : (
                                        usage.context_label || usage.context_type
                                    )}
                                </div>
                            ))}
                            {row.where_used.length > 0 && (
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 small"
                                    onClick={() => setUsageItem(row)}
                                >
                                    View all ({row.where_used.length})
                                </Button>
                            )}
                        </>
                    )}
                </div>
            ),
            grow: 2,
        },
        {
            name: 'Added',
            selector: (row: DocumentLibraryItem) => row.date_added ? moment(row.date_added).format('YYYY-MM-DD') : '',
            maxWidth: '120px',
        },
        {
            name: 'Revised',
            selector: (row: DocumentLibraryItem) => row.date_revised ? moment(row.date_revised).format('YYYY-MM-DD') : '',
            maxWidth: '120px',
        },
        {
            name: 'Actions',
            cell: (row: DocumentLibraryItem) => (
                <div className="d-flex flex-column gap-1 py-2">
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            setEditingItem(row);
                            setShowCreateModal(true);
                        }}
                    >
                        Edit
                    </Button>
                    {row.type === 'internal_doc' ? (
                        <>
                            {shouldShowOpenButton(row) && (
                                <Button size="sm" className="btn-teal" onClick={() => openInternal(row, false)}>
                                    Open
                                </Button>
                            )}
                            <Button size="sm" variant="secondary" onClick={() => openInternal(row, true)}>
                                Download
                            </Button>
                        </>
                    ) : (
                        shouldShowOpenButton(row) && (
                            <Button size="sm" className="btn-teal" onClick={() => openExternal(row)}>
                                Open
                            </Button>
                        )
                    )}
                    <Button size="sm" variant="outline-danger" onClick={() => handleDeleteItem(row)}>
                        Delete
                    </Button>
                </div>
            ),
            width: '130px',
        },
    ];

    if (organizationTeam.isLoading && !organizationTeam.data) {
        return <Loading centerPage={true} />;
    }

    const handleBackToTeam = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        if (organization_id && team_id) {
            navigate(`/organizations/${organization_id}/teams/${team_id}/collab`);
        }
    };

    return (
        <>
            <TeamHeader suffixRoute="/document-library" />
            <div className="container mb-5">
                <div className="row mt-4">
                    <div className="col-12 col-md-8">
                        <h2>Library</h2>
                        <p className="text-muted mb-0">
                            Search and manage links and documents for this team.
                        </p>
                        <p className="small text-muted mb-0 mt-2">
                            {loading
                                ? 'Loading documents...'
                                : pagination.total === 0
                                    ? '0 results'
                                    : `Showing ${pagination.from}-${pagination.to} of ${pagination.total}`}
                        </p>
                    </div>
                    <div className="col-12 col-md-4 mt-3 mt-md-0 text-md-end">
                        <Button variant="secondary" className="me-2" onClick={handleBackToTeam}>
                            Back to Team
                        </Button>
                        <Button className="btn-teal" onClick={() => {
                            setEditingItem(null);
                            setShowCreateModal(true);
                        }}>
                            Add Document
                        </Button>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-12">
                        <DataTable
                            columns={columns as any}
                            data={items}
                            progressPending={loading}
                            pagination
                            paginationServer
                            paginationTotalRows={pagination.total}
                            paginationDefaultPage={pagination.current_page}
                            paginationResetDefaultPage={paginationResetToggle}
                            paginationPerPage={pagination.per_page}
                            paginationRowsPerPageOptions={[15, 30, 50]}
                            onChangePage={(page) => {
                                fetchItems(page, pagination.per_page);
                            }}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                fetchItems(page, newPerPage);
                            }}
                            subHeader
                            subHeaderComponent={subHeaderComponentMemo}
                            noDataComponent="No documents found for this team."
                            persistTableHead
                        />
                    </div>
                </div>
            </div>

            <CreateDocumentModal
                show={showCreateModal}
                onHide={() => {
                    setShowCreateModal(false);
                    setEditingItem(null);
                }}
                onCreated={(mode) => {
                    if (mode === 'created') {
                        setPaginationResetToggle((current) => !current);
                        fetchItems(1, pagination.per_page);
                        return;
                    }

                    fetchItems(pagination.current_page, pagination.per_page);
                }}
                organizationId={organization_id}
                teamId={team_id}
                item={editingItem}
            />
            <UsageListModal
                show={usageItem !== null}
                onHide={() => setUsageItem(null)}
                item={usageItem}
                onDetachUsage={handleDetachUsage}
                detachingUsageId={detachingUsageId}
            />
        </>
    );
};
